import { apiRequest } from './client';
import type {
  TelephonyConnectionWithProvider,
  TwilioConnection,
  ConnectConnection,
  VoxtronConnection,
  PhoneNumber,
  ConnectionWebhooksResponse,
  CreatePhoneNumberInput,
  UpdatePhoneNumberInput,
  Provider,
  CreateTelephonyConnectionInput,
  ProvisionTelephonyInput,
  UpdateTwilioConnectionInput,
  UpdateConnectConnectionInput,
  UpdateVoxtronConnectionInput,
  TelephonyDefaults,
  UpdateTelephonyDefaultsInput,
  UUID,
} from '@/types';

export const telephonyApi = {
  listProviders(): Promise<Provider[]> {
    return apiRequest('/api/v1/providers');
  },

  listConnections(tenantId: UUID): Promise<TelephonyConnectionWithProvider[]> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections`, { tenantId });
  },

  getConnection(tenantId: UUID, connectionId: UUID): Promise<TelephonyConnectionWithProvider> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}`, { tenantId });
  },

  updateConnection(
    tenantId: UUID,
    connectionId: UUID,
    input: { name?: string; status?: string }
  ): Promise<TelephonyConnectionWithProvider> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}`, {
      method: 'PATCH',
      body: input,
      tenantId,
    });
  },

  listAvailableTwilioNumbers(
    tenantId: UUID,
    connectionId: UUID
  ): Promise<{
    items: Array<{
      e164: string;
      friendly_name: string | null;
      sid: string | null;
      phone_number_id: string | null;
      assigned_user_id: string | null;
      assigned_user_name: string | null;
      in_inventory: boolean;
      assignment_status: string;
    }>;
  }> {
    return apiRequest(
      `/api/v1/tenants/${tenantId}/connections/${connectionId}/phone-numbers/available`,
      { tenantId }
    );
  },

  createConnection(
    tenantId: UUID,
    input: CreateTelephonyConnectionInput | ProvisionTelephonyInput
  ): Promise<TelephonyConnectionWithProvider> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections`, {
      method: 'POST',
      body: input,
      tenantId,
    });
  },

  getDefaults(tenantId: UUID): Promise<TelephonyDefaults> {
    return apiRequest(`/api/v1/tenants/${tenantId}/telephony/defaults`, { tenantId });
  },

  updateDefaults(tenantId: UUID, input: UpdateTelephonyDefaultsInput): Promise<TelephonyDefaults> {
    return apiRequest(`/api/v1/tenants/${tenantId}/telephony/defaults`, {
      method: 'PUT',
      body: input,
      tenantId,
    });
  },

  getTwilioConfig(tenantId: UUID, connectionId: UUID): Promise<TwilioConnection> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/twilio`, { tenantId });
  },

  updateTwilioConfig(
    tenantId: UUID,
    connectionId: UUID,
    input: UpdateTwilioConnectionInput
  ): Promise<TwilioConnection> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/twilio`, {
      method: 'PUT',
      body: input,
      tenantId,
    });
  },

  getConnectConfig(tenantId: UUID, connectionId: UUID): Promise<ConnectConnection> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/connect`, { tenantId });
  },

  updateConnectConfig(
    tenantId: UUID,
    connectionId: UUID,
    input: UpdateConnectConnectionInput
  ): Promise<ConnectConnection> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/connect`, {
      method: 'PUT',
      body: input,
      tenantId,
    });
  },

  getVoxtronConfig(tenantId: UUID, connectionId: UUID): Promise<VoxtronConnection> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/voxtron`, {
      tenantId,
    });
  },

  updateVoxtronConfig(
    tenantId: UUID,
    connectionId: UUID,
    input: UpdateVoxtronConnectionInput
  ): Promise<VoxtronConnection> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/voxtron`, {
      method: 'PUT',
      body: input,
      tenantId,
    });
  },

  testConnection(tenantId: UUID, connectionId: UUID): Promise<{ success: boolean; message: string }> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/test`, {
      method: 'POST',
      tenantId,
    });
  },

  listPhoneNumbers(tenantId: UUID, connectionId?: UUID): Promise<PhoneNumber[]> {
    const path = connectionId
      ? `/api/v1/tenants/${tenantId}/connections/${connectionId}/phone-numbers`
      : `/api/v1/tenants/${tenantId}/numbers`;
    return apiRequest(path, { tenantId });
  },

  createPhoneNumber(
    tenantId: UUID,
    connectionId: UUID,
    input: CreatePhoneNumberInput
  ): Promise<PhoneNumber> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/phone-numbers`, {
      method: 'POST',
      body: input,
      tenantId,
    });
  },

  updatePhoneNumber(
    tenantId: UUID,
    connectionId: UUID,
    phoneNumberId: UUID,
    input: UpdatePhoneNumberInput
  ): Promise<PhoneNumber> {
    return apiRequest(
      `/api/v1/tenants/${tenantId}/connections/${connectionId}/phone-numbers/${phoneNumberId}`,
      { method: 'PATCH', body: input, tenantId }
    );
  },

  deletePhoneNumber(
    tenantId: UUID,
    connectionId: UUID,
    phoneNumberId: UUID
  ): Promise<{ deleted: boolean; id: string; note?: string }> {
    return apiRequest(
      `/api/v1/tenants/${tenantId}/connections/${connectionId}/phone-numbers/${phoneNumberId}`,
      { method: 'DELETE', tenantId }
    );
  },

  clearUnassignedPhoneNumbers(
    tenantId: UUID,
    connectionId: UUID
  ): Promise<{ removed: number; note?: string }> {
    return apiRequest(
      `/api/v1/tenants/${tenantId}/connections/${connectionId}/phone-numbers/clear-unassigned`,
      { method: 'POST', tenantId }
    );
  },

  syncPhoneNumbers(tenantId: UUID, connectionId: UUID): Promise<PhoneNumber[]> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/phone-numbers/sync`, {
      method: 'POST',
      tenantId,
    });
  },

  getWebhooks(tenantId: UUID, connectionId: UUID): Promise<ConnectionWebhooksResponse> {
    return apiRequest(`/api/v1/tenants/${tenantId}/connections/${connectionId}/webhooks`, { tenantId });
  },
};
